import axios from './axios'
import { getFirebaseErrorMessage } from '@/utils/firebaseErrorHandler'
import type { PasswordActionData } from '@/enums/passwordActionType'

export interface LoginCredentials {
  email: string
  password: string
  rememberMe?: boolean
  companyId?: string
  user?: any // For skipping password update
  token?: string // For skipping password update
}

export interface SocialLoginCredentials {
  provider: 'Google' | 'Microsoft'
  token: string
}

export interface PhoneLoginCredentials {
  phoneNumber: string
}

export interface PhoneOTPVerificationCredentials {
  phoneNumber: string
  otp: string
}

export interface NewPasswordCredentials {
  email: string
  newPassword: string
  session: string
}

export interface ForgotPasswordData {
  email: string
}

export interface ResetPasswordData {
  email: string
  code: string
  newPassword: string
}

export interface JWTLoginCredentials {
  token: string
}

class AuthService {
  async login(credentials: LoginCredentials) {
   
    try {
      const response = await axios.post('/auth/login', credentials, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })
     

      // Handle NEW_PASSWORD_REQUIRED challenge
      if (response.data.challengeName === 'NEW_PASSWORD_REQUIRED') {
        return {
          success: true,
          challengeName: response.data.challengeName,
          session: response.data.session
        }
      }

      // For successful login without challenge
      if (!response.data.success || !response.data.accessToken || !response.data.user) {
        throw new Error('Invalid response format from server')
      }

      return response.data
    } catch (error) {
      console.error('Auth service: Login failed:', error)
      // Type assertion for error to include response property
      const axiosError = error as { response?: { data?: { message?: string, warning?: string }, status?: number } };
      const serverMessage = axiosError.response?.data?.message ?? '';
      const warningMessage = axiosError.response?.data?.warning ?? '';

      // Handle rate limiting (429 Too Many Requests)
      if (serverMessage.includes("Too many login attempts")) {
        throw new Error(serverMessage || 'Too many login attempts. Please try again later.');
      }

      // Handle 401 errors with separate warning messages
      if (warningMessage) {
        if (warningMessage) {
          // Create a custom error with both message and warning
          const customError = new Error(warningMessage) // Use the actual error message, not the warning
          throw customError
        } else {
          // Just the error message
          throw new Error(serverMessage)
        }
      }

      // Handle Firebase authentication errors
      const userFriendlyMessage = getFirebaseErrorMessage(error)
      const customError = new Error(userFriendlyMessage)
      
      // Preserve the original error for debugging
      ;(customError as any).originalError = error
      
      throw customError
    }
  }

  async initiatePhoneLogin(credentials: PhoneLoginCredentials) {
   
    try {
      const response = await axios.post('/auth/phone/initiate', credentials)
     
      
      if (!response.data.success) {
        throw new Error('Phone login initiation failed: ' + (response.data.message || 'Unknown error'))
      }
      
      return response.data
    } catch (error) {
      console.error('Auth service: Phone login initiation failed:', error)
      throw error
    }
  }

  async verifyPhoneOTP(credentials: PhoneOTPVerificationCredentials) {
 
    try {
      const response = await axios.post('/auth/phone/verify', credentials)
    
      
      if (!response.data.success) {
        throw new Error('Phone OTP verification failed: ' + (response.data.message || 'Unknown error'))
      }
      
      return response.data
    } catch (error) {
      console.error('Auth service: Phone OTP verification failed:', error)
      throw error
    }
  }

  async resendPhoneOTP(credentials: PhoneLoginCredentials) {
    try {
      const response = await axios.post('/auth/phone/resend', credentials)
      
      if (!response.data.success) {
        throw new Error('Phone OTP resend failed: ' + (response.data.message || 'Unknown error'))
      }
      
      return response.data
    } catch (error) {
      console.error('Auth service: Phone OTP resend failed:', error)
      throw error
    }
  }

  async completeNewPassword(data: NewPasswordCredentials) {
    const response = await axios.post('/auth/complete-new-password', data)
    return response.data
  }

  async skipNewPassword(data: { email: string, password: string }) {
   
    try {
      const response = await axios.post('/auth/skip-password', data)
     
      
      if (!response.data.success) {
        throw new Error('Skip password failed: ' + (response.data.message || 'Unknown error'))
      }
      
      return response.data
    } catch (error) {
      console.error('Auth service: Skip password failed:', error)
      throw error
    }
  }

  async logout() {
    const response = await axios.post('/auth/logout')
    return response.data
  }

  async forgotPassword(data: ForgotPasswordData) {
    const response = await axios.post('/auth/forgot-password', data)
    return response.data
  }

  async resetPassword(data: ResetPasswordData) {
    const response = await axios.post('/auth/reset-password', data)
    return response.data
  }

  async getMe() {
    try {
      const response = await axios.get('/auth/me')
      if (!response.data.success || !response.data.data) {
        throw new Error('Invalid response from /me endpoint')
      }
      return {
        success: true,
        data: response.data.data
      }
    } catch (error) {
      console.error('Get me failed:', error)
      throw error
    }
  }

  async refreshToken() {
    try {
      // Don't send the expired token in the Authorization header for refresh
      const response = await axios.post('/auth/refresh-token', {}, {
        headers: {
          Authorization: undefined // Clear the Authorization header
        }
      })
      
      if (!response.data.success || !response.data.accessToken) {
        throw new Error('Invalid refresh token response')
      }
      
      return response.data
    } catch (error) {
      console.error('Token refresh failed:', error)
      // Rethrow to let the auth store handle the failure
      throw error
    }
  }

  async loginWithSocial(credentials: SocialLoginCredentials) {
   
    try {
      const response = await axios.post('/auth/login-social', credentials)
     

      if (!response.data.success || !response.data.accessToken || !response.data.user) {
        throw new Error('Invalid response format from server')
      }

      return response.data
    } catch (error) {
      console.error('Auth service: Social login failed:', error)
      throw error
    }
  }

  async loginWithJWT(credentials: JWTLoginCredentials) {
    try {
      const response = await axios.post('/auth/jwt-login', credentials, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })

      if (!response.data.success || !response.data.accessToken || !response.data.user) {
        throw new Error('Invalid response format from server')
      }

      return response.data
    } catch (error) {
      console.error('Auth service: JWT login failed:', error)
      throw error
    }
  }

  async logPasswordAction(credentials: PasswordActionData) {
    const response = await axios.post('/auth/log-password-action', credentials)
    return response.data
  }
}

export default new AuthService() 